import * as React from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { Badge } from "./badge";
import { Button } from "./button";

interface MultiSelectProps {
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Optionnel : traduit l'affichage d'une option sans changer sa valeur stockée. */
  getLabel?: (option: string) => string;
}

export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = "Sélectionner...",
  disabled = false,
  getLabel = (o) => o,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (option: string) => {
    if (value.includes(option)) {
      onChange(value.filter((v) => v !== option));
    } else {
      onChange([...value, option]);
    }
  };

  const removeOption = (option: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== option));
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        className={`flex min-h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background ${
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
        }`}
        onClick={() => !disabled && setOpen(!open)}
      >
        <div className="flex flex-wrap gap-1 flex-1">
          {value.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            value.map((v) => (
              <Badge
                key={v}
                variant="secondary"
                className="text-xs px-2 py-0.5 flex items-center gap-1"
              >
                {getLabel(v)}
                {!disabled && (
                  <X
                    className="h-3 w-3 cursor-pointer hover:text-destructive"
                    onClick={(e) => removeOption(v, e)}
                  />
                )}
              </Badge>
            ))
          )}
        </div>
        <ChevronDown className={`h-4 w-4 shrink-0 opacity-50 transition-transform ${open ? "rotate-180" : ""}`} />
      </div>

      {open && !disabled && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover p-1 shadow-md">
          {options.map((option) => {
            const isSelected = value.includes(option);
            return (
              <div
                key={option}
                className={`relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground ${
                  isSelected ? "bg-accent/50" : ""
                }`}
                onClick={() => toggleOption(option)}
              >
                <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${
                  isSelected ? "border-primary bg-primary text-primary-foreground" : "border-muted"
                }`}>
                  {isSelected && <Check className="h-3 w-3" />}
                </div>
                {getLabel(option)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
